import { calculateTcyValue, CalendarDay } from 'further-ui/utils';
import { ApiResponses } from 'lib/trpcClient';
import { round } from 'lodash';
import { match } from 'ts-pattern';
import {
  CommitmentData,
  FullCapitalActionsTotals,
  FundPerformanceData,
  IncomeAndExpenseEntry,
  LiveHoldingByClass,
  OverviewData,
  PreferredReturnBalance,
  RealisedHoldingByClass,
} from '../types';

const mapFundPerformance = (
  rawData:
    | ApiResponses['commitments']['get']['commitmentBalance']['commitmentMetrics']
    | ApiResponses['commitments']['getInvestorBalance']['commitmentMetrics'],
  exchangeRate: number,
): FundPerformanceData => {
  return {
    tvpi: {
      value: rawData.tvpi,
      valueLcy: rawData.tvpiValueLcy,
      valueTcy: calculateTcyValue(rawData.tvpiValueLcy, {
        fetchedRate: exchangeRate,
      }),
      symbol: 'multiple',
    },
    dpi: {
      value: rawData.dpi,
      valueLcy: rawData.dpiValueLcy,
      valueTcy: calculateTcyValue(rawData.tvpiValueLcy, {
        fetchedRate: exchangeRate,
      }),
      symbol: 'multiple',
    },
    rvpi: {
      value: rawData.rvpi,
      valueLcy: rawData.rvpiValueLcy,
      valueTcy: calculateTcyValue(rawData.tvpiValueLcy, {
        fetchedRate: exchangeRate,
      }),
      symbol: 'multiple',
    },
    grossMoic: {
      value: rawData.grossMoic,
      symbol: 'multiple',
    },
    netMoic: {
      value: rawData.netMoic,
      symbol: 'multiple',
    },
    grossIrr: {
      value: rawData.grossIrr,
      symbol: 'percentage',
    },
    netIrr: {
      value: rawData.netIrr,
      symbol: 'percentage',
    },
  };
};

const mapOverview = (
  input: {
    totalCommitmentLcy: number;
    paidInCapitalLcy: number;
    unfundedCommitmentLcy: number;
    liveCapitalOnAccountLcy: number;
  },
  exchangeRate: number,
): Pick<
  OverviewData,
  | 'totalCommitment'
  | 'paidInCapital'
  | 'unfundedCommitment'
  | 'liveCapitalOnAccount'
> => {
  return {
    totalCommitment: {
      valueLcy: input.totalCommitmentLcy,
      valueTcy: calculateTcyValue(input.totalCommitmentLcy, {
        fetchedRate: exchangeRate,
      }),
    },
    paidInCapital: {
      valueLcy: input.paidInCapitalLcy,
      valueTcy: calculateTcyValue(input.paidInCapitalLcy, {
        fetchedRate: exchangeRate,
      }),
      percentageOfTotal: input.paidInCapitalLcy / input.totalCommitmentLcy,
    },
    unfundedCommitment: {
      valueLcy: input.unfundedCommitmentLcy,
      valueTcy: calculateTcyValue(input.unfundedCommitmentLcy, {
        fetchedRate: exchangeRate,
      }),
      percentageOfTotal: input.unfundedCommitmentLcy / input.totalCommitmentLcy,
    },
    liveCapitalOnAccount: {
      valueLcy: input.liveCapitalOnAccountLcy,
      valueTcy: calculateTcyValue(input.liveCapitalOnAccountLcy, {
        fetchedRate: exchangeRate,
      }),
    },
  };
};

const mapFees = (
  rawData:
    | ApiResponses['commitments']['get']['commitmentBalance']['fees']
    | ApiResponses['commitments']['getInvestorBalance']['fees'],
): Pick<CommitmentData, 'futureFeesAndCharges' | 'feesAndCharges'> => {
  const mappedFees = rawData.map((fee) => ({
    id: fee.id,
    name: fee.name,
    date: fee.accrualDate,
    calculationBasis: fee.calculationBasis,
    salesTaxValueLcy: fee.salesTaxAmountLcy,
    valueExTaxLcy: fee.feeExTaxLcy,
    valueIncTaxLcy: fee.feeIncTaxLcy,
    isFutureFee: fee.isFutureFee,
    status: match(fee)
      .with({ isFutureFee: true }, () => 'Not due')
      .with({ isApproved: true }, () => 'Approved')
      .otherwise(() => 'Unapproved'),
  }));

  return {
    futureFeesAndCharges: mappedFees.filter((fee) => fee.isFutureFee),
    feesAndCharges: mappedFees.filter((fee) => !fee.isFutureFee),
  };
};

const mapIncomeAndExpenses = (
  rawData:
    | ApiResponses['commitments']['get']['commitmentBalance']['incomesAndExpenses']
    | ApiResponses['commitments']['getInvestorBalance']['incomesAndExpenses'],
): Array<IncomeAndExpenseEntry> => {
  return rawData.map((entry) => ({
    id: entry.id,
    action: entry.description,
    date: entry.date,
    valueLcy: entry.valueLcy,
    category: entry.incomeOrExpenseType,
  }));
};

const mapPreferredReturnBalance = (
  rawData:
    | ApiResponses['commitments']['get']['commitmentBalance']['preferredReturn']
    | ApiResponses['commitments']['getInvestorBalance']['preferredReturn'],
): PreferredReturnBalance => {
  return {
    accruedPreferredReturnLcy: rawData.accruedPreferredReturnLcy,
    paidPreferredReturnLcy: rawData.paidPreferredReturnLcy,
    outstandingPreferredReturnLcy: rawData.outstandingPreferredReturnLcy,
  };
};

const mapCapitalBalanceAndReconciledPayments = (
  rawData:
    | ApiResponses['commitments']['get']['commitmentBalance']['reconciledActions']
    | ApiResponses['commitments']['getInvestorBalance']['reconciledActions'],
): CommitmentData['capitalBalanceAndReconciledPayments'] => {
  return {
    data: rawData.map((action) => ({
      id: action.actionId,
      date: action.ledgerDate,
      valueLcy: action.reconciledAmountLcy,
      runningBalanceLcy: action.runningBalanceLcy,
      type: action.actionRef,
    })),
    totals: {
      valueLcy: rawData.reduce(
        (total, action) => total + action.reconciledAmountLcy,
        0,
      ),
    },
  };
};

const mapCapitalActions = (
  rawData:
    | ApiResponses['commitments']['get']['commitmentBalance']['capitalActions']
    | ApiResponses['commitments']['getInvestorBalance']['capitalActions'],
): CommitmentData['capitalActions'] => {
  const totals = rawData.reduce<FullCapitalActionsTotals>(
    (totals, { calledCapital, distributedCapital }) => {
      return {
        insideCommitmentAmountLcy:
          totals.insideCommitmentAmountLcy +
          calledCapital.insideCommitmentCalledCapital,
        outsideCommitmentAmountLcy:
          totals.outsideCommitmentAmountLcy +
          calledCapital.outsideCommitmentCalledCapital,
        recalledCapitalLcy:
          totals.recalledCapitalLcy + calledCapital.recalledCapital,
        equalisationInterestLcy:
          totals.equalisationInterestLcy + calledCapital.equalisationInterest,
        notionalPreCarryDistributionLcy:
          totals.notionalPreCarryDistributionLcy +
          distributedCapital.notionalPreCarryDistribution,
        notionalCarriedInterestLcy:
          totals.notionalCarriedInterestLcy + distributedCapital.notionalCarry,
        netDistributionInsideLcy:
          totals.netDistributionInsideLcy + distributedCapital.netDistribution,
        recallableDistributionLcy:
          totals.recallableDistributionLcy +
          distributedCapital.recallableDistribution,
        netDistributionOutsideLcy: 0,
        capitalRefundInsideCommitmentLcy:
          totals.capitalRefundInsideCommitmentLcy +
          distributedCapital.capitalRefundInsideCommitment,
        capitalRefundOutsideOfCommitmentLcy:
          totals.capitalRefundOutsideOfCommitmentLcy +
          distributedCapital.capitalRefundOutsideOfCommitment,
      };
    },
    {
      insideCommitmentAmountLcy: 0,
      outsideCommitmentAmountLcy: 0,
      recalledCapitalLcy: 0,
      equalisationInterestLcy: 0,
      notionalPreCarryDistributionLcy: 0,
      notionalCarriedInterestLcy: 0,
      netDistributionInsideLcy: 0,
      netDistributionOutsideLcy: 0,
      recallableDistributionLcy: 0,
      capitalRefundInsideCommitmentLcy: 0,
      capitalRefundOutsideOfCommitmentLcy: 0,
    },
  );

  return {
    fullDetails: {
      data: rawData.map(({ calledCapital, distributedCapital, ...action }) => ({
        id: action.capitalActionId,
        date: action.ledgerDate,
        name: action.name,
        insideCommitmentAmountLcy: calledCapital.insideCommitmentCalledCapital,
        outsideCommitmentAmountLcy:
          calledCapital.outsideCommitmentCalledCapital,
        recalledCapitalLcy: calledCapital.recalledCapital,
        equalisationInterestLcy: calledCapital.equalisationInterest,
        notionalPreCarryDistributionLcy:
          distributedCapital.notionalPreCarryDistribution,
        notionalCarriedInterestLcy: distributedCapital.notionalCarry,
        netDistributionInsideLcy: distributedCapital.netDistribution,
        recallableDistributionLcy: distributedCapital.recallableDistribution,
        capitalRefundInsideCommitmentLcy:
          distributedCapital.capitalRefundInsideCommitment,
        capitalRefundOutsideOfCommitmentLcy:
          distributedCapital.capitalRefundOutsideOfCommitment,
        // value currently not in system
        netDistributionOutsideLcy: 0,
        priorUnfundedCommitmentLcy: action.priorUnfundedCommitmentAmountLcy,
        newUnfundedCommitmentLcy: action.newUnfundedCommitmentAmountLcy,
        unfundedCommitmentPercentage: action.unfundedCommitmentPercentage,
      })),
      totals,
    },
    summary: rawData.map((action) => ({
      id: action.capitalActionId,
      date: action.ledgerDate,
      name: action.name,
      capitalCallLcy: action.capitalCalledLcy,
      capitalRefundLcy: action.capitalDistributedLcy,
      netCallLcy: round(
        action.capitalCalledLcy - action.capitalDistributedLcy,
        2,
      ),
      reconciliationStatus: action.reconciliationStatus,
    })),
  };
};

const getEarliestDate = (holdings: Array<CalendarDay>): CalendarDay => {
  return holdings.reduce((earliest, current) =>
    current.isBefore(earliest) ? current : earliest,
  );
};

const mapHoldings = ({
  liveHoldings,
  liveHoldingsByAssetSummary,
  liveHoldingsTotals,
  realisedHoldings,
  realisedHoldingsByAssetSummary,
  realisedHoldingsTotals,
}: {
  liveHoldings:
    | ApiResponses['commitments']['get']['commitmentBalance']['liveHoldings']
    | ApiResponses['commitments']['getInvestorBalance']['liveHoldings'];
  liveHoldingsByAssetSummary:
    | ApiResponses['commitments']['get']['commitmentBalance']['liveHoldingsByAssetSummary']
    | ApiResponses['commitments']['getInvestorBalance']['liveHoldingsByAssetSummary'];
  liveHoldingsTotals:
    | ApiResponses['commitments']['get']['commitmentBalance']['liveHoldingsTotals']
    | ApiResponses['commitments']['getInvestorBalance']['liveHoldingsTotals'];
  realisedHoldings:
    | ApiResponses['commitments']['get']['commitmentBalance']['realisedHoldings']
    | ApiResponses['commitments']['getInvestorBalance']['realisedHoldings'];
  realisedHoldingsTotals:
    | ApiResponses['commitments']['get']['commitmentBalance']['realisedHoldingsTotals']
    | ApiResponses['commitments']['getInvestorBalance']['realisedHoldingsTotals'];
  realisedHoldingsByAssetSummary:
    | ApiResponses['commitments']['get']['commitmentBalance']['realisedHoldingsByAssetSummary']
    | ApiResponses['commitments']['getInvestorBalance']['realisedHoldingsByAssetSummary'];
}): Pick<CommitmentData, 'liveHoldings' | 'realisedHoldings'> => {
  const liveHoldingsByClass: Array<LiveHoldingByClass> = liveHoldings.map(
    (liveHolding) => ({
      id: liveHolding.id,
      assetId: liveHolding.assetId,
      assetName: liveHolding.assetName,
      currentHoldingValueLcy: liveHolding.currentHoldValueLcy,
      investedCapitalLcy: liveHolding.investedCapitalLcy,
      grossMoic: liveHolding.grossMoic,
      grossIrr: liveHolding.grossIrr,
      shareClassName: liveHolding.shareClassName,
      initialUnitValueLcy: liveHolding.initialUnitValueLcy,
      investmentDate: liveHolding.investmentDate,
      presentUnitValueLcy: liveHolding.currentUnitValueLcy,
      proRatedHoldingUnits: liveHolding.holdingUnits,
      assetImage: liveHolding.assetImage,
    }),
  );

  const realisedHoldingsByClass: Array<RealisedHoldingByClass> =
    realisedHoldings.map((realisedHolding) => ({
      id: realisedHolding.id,
      assetId: realisedHolding.assetId,
      assetName: realisedHolding.assetName,
      investedCapitalLcy: realisedHolding.investedCapitalLcy,
      grossMoic: realisedHolding.grossMoic,
      grossIrr: realisedHolding.grossIrr,
      shareClassName: realisedHolding.shareClassName,
      proRatedUnitsRealised: realisedHolding.realisedUnits,
      investmentDate: realisedHolding.investmentDate,
      realisationDate: realisedHolding.realisationDate,
      realisationValueLcy: realisedHolding.realisationValueLcy,
      assetImage: realisedHolding.assetImage,
    }));
  return {
    liveHoldings: {
      byClass: {
        data: liveHoldingsByClass,
        totals: {
          currentHoldingValueLcy: liveHoldingsTotals.currentHoldValueLcy,
          investedCapitalLcy: liveHoldingsTotals.investedCapitalLcy,
          grossMoic: liveHoldingsTotals.grossMoic,
          grossIrr: liveHoldingsTotals.grossIrr,
        },
      },
      byHolding: {
        data: liveHoldingsByAssetSummary.map((liveHolding) => {
          const classData = liveHoldingsByClass.filter(
            (holding) => holding.assetId === liveHolding.assetId,
          );
          return {
            id: liveHolding.assetId,
            assetId: liveHolding.assetId,
            assetName: liveHolding.assetName,
            investmentDate: getEarliestDate(
              classData.map((holding) => holding.investmentDate),
            ),
            currentHoldingValueLcy: liveHolding.currentHoldValueLcy,
            investedCapitalLcy: liveHolding.investedCapitalLcy,
            grossMoic: liveHolding.grossMoic,
            grossIrr: liveHolding.grossIrr,
            classes: classData,
          };
        }),
        totals: {
          currentHoldingValueLcy: liveHoldingsTotals.currentHoldValueLcy,
          investedCapitalLcy: liveHoldingsTotals.investedCapitalLcy,
          grossMoic: liveHoldingsTotals.grossMoic,
          grossIrr: liveHoldingsTotals.grossIrr,
        },
      },
    },
    realisedHoldings: {
      byClass: {
        data: realisedHoldingsByClass,
        totals: {
          investedCapitalLcy: realisedHoldingsTotals.investedCapitalLcy,
          realisationValueLcy: realisedHoldingsTotals.realisationValueLcy,
          grossMoic: realisedHoldingsTotals.grossMoic,
          grossIrr: realisedHoldingsTotals.grossIrr,
        },
      },
      byHolding: {
        data: realisedHoldingsByAssetSummary.map((realisedHolding) => {
          const classData = realisedHoldingsByClass.filter(
            (holding) => holding.assetId === realisedHolding.assetId,
          );
          return {
            id: realisedHolding.assetId,
            assetId: realisedHolding.assetId,
            assetName: realisedHolding.assetName,
            realisationDate: getEarliestDate(
              classData.map((holding) => holding.realisationDate),
            ),
            investmentDate: getEarliestDate(
              classData.map((holding) => holding.investmentDate),
            ),
            realisationValueLcy: realisedHolding.realisationValueLcy,
            investedCapitalLcy: realisedHolding.investedCapitalLcy,
            grossMoic: realisedHolding.grossMoic,
            grossIrr: realisedHolding.grossIrr,
            classes: classData,
          };
        }),
        totals: {
          investedCapitalLcy: realisedHoldingsTotals.investedCapitalLcy,
          realisationValueLcy: realisedHoldingsTotals.realisationValueLcy,
          grossMoic: realisedHoldingsTotals.grossMoic,
          grossIrr: realisedHoldingsTotals.grossIrr,
        },
      },
    },
  };
};

export const mapCommitmentResponseToCommitmentData = (
  rawData: ApiResponses['commitments']['get'],
): CommitmentData => {
  const {
    currencySettings,
    commitmentBalance: {
      commitmentMetrics,
      liveHoldings,
      liveHoldingsByAssetSummary,
      liveHoldingsTotals,
      realisedHoldings,
      realisedHoldingsByAssetSummary,
      realisedHoldingsTotals,
      fees,
      preferredReturn,
      capitalActions,
      incomesAndExpenses,
      reconciledActions,
      ...commitmentBalance
    },
  } = rawData;

  const fetchedRate = currencySettings.exchangeRateLcyToInvestor;

  return {
    id: rawData.id,
    hasDocuments: rawData.hasDocuments,
    investorName: rawData.investorName ?? '',
    closeDate: rawData.closeDate,
    vehicleId: rawData.vehicleId,
    commitmentDate: new CalendarDay(rawData.commitmentDate),
    currencySettings,
    fundPerformance: mapFundPerformance(commitmentMetrics, fetchedRate),
    overview: {
      ...mapOverview(
        {
          paidInCapitalLcy: commitmentBalance.paidInCapitalLcy,
          liveCapitalOnAccountLcy: commitmentBalance.liveCapitalOnAccountLcy,
          totalCommitmentLcy: commitmentBalance.totalCommitmentLcy,
          unfundedCommitmentLcy: commitmentBalance.unfundedCommitmentLcy,
        },
        fetchedRate,
      ),
      capitalCommitmentLcy: rawData.capitalCommitmentLcy,
      loanCommitmentLcy: rawData.loanCommitmentLcy,
      advanceSubscriptionBalanceLcy:
        commitmentBalance.advanceSubscriptionBalanceLcy,
      outsideOfCommitmentPaidInCapitalLcy:
        commitmentBalance.outsideOfCommitmentPaidInCapitalLcy,
      feeTemplate: rawData.feeTemplate
        ? {
            id: rawData.feeTemplate.id ?? '',
            name: rawData.feeTemplate.name ?? '',
          }
        : undefined,
      carriedInterestFeeTemplate: rawData.carriedInterestFeeTemplate
        ? {
            id: rawData.carriedInterestFeeTemplate.id ?? '',
            name: rawData.carriedInterestFeeTemplate.name ?? '',
          }
        : undefined,
      equalisationInterestFeeTemplate: rawData.equalisationInterestFeeTemplateId
        ? {
            id: rawData.equalisationInterestFeeTemplate.id ?? '',
            name: rawData.equalisationInterestFeeTemplate.name ?? '',
          }
        : undefined,
    },
    ...mapHoldings({
      liveHoldings,
      liveHoldingsByAssetSummary,
      liveHoldingsTotals,
      realisedHoldings,
      realisedHoldingsByAssetSummary,
      realisedHoldingsTotals,
    }),
    ...mapFees(fees),
    closeHistory: {
      data: [],
      totals: {
        commitmentValueLcy: 0,
        netEqualisationInterestLcy: 0,
      },
    },
    preferredReturnBalance: mapPreferredReturnBalance(preferredReturn),
    capitalBalanceAndReconciledPayments:
      mapCapitalBalanceAndReconciledPayments(reconciledActions),
    incomeAndExpenses: mapIncomeAndExpenses(incomesAndExpenses),
    capitalActions: mapCapitalActions(capitalActions),
  };
};

export const mapInvestorBalanceResponseToCommitmentData = (
  rawDataInput: ApiResponses['commitments']['getInvestorBalance'],
): CommitmentData => {
  const {
    balanceDate,
    commitmentIds,
    commitmentMetrics,
    incomesAndExpenses,
    reconciledActions,
    preferredReturn,
    capitalActions,
    fundCloseHistory,
    liveHoldings,
    liveHoldingsByAssetSummary,
    liveHoldingsTotals,
    realisedHoldings,
    realisedHoldingsByAssetSummary,
    realisedHoldingsTotals,
    fees,
    investorName,
    currencySettings,
    initialDate,
    ...rawData
  } = rawDataInput;

  const fetchedRate = currencySettings.exchangeRateLcyToInvestor;

  return {
    hasDocuments: rawData.hasDocuments,
    id: commitmentIds.join('-'),
    currencySettings,
    investorName,
    commitmentDate: initialDate,
    vehicleId: rawData.vehicleId,
    fundPerformance: mapFundPerformance(commitmentMetrics, fetchedRate),
    overview: {
      ...mapOverview(
        {
          paidInCapitalLcy: rawData.paidInCapitalLcy,
          totalCommitmentLcy: rawData.totalCommitmentLcy,
          unfundedCommitmentLcy: rawData.unfundedCommitmentLcy,
          liveCapitalOnAccountLcy: rawData.liveCapitalOnAccountLcy,
        },
        fetchedRate,
      ),
      advanceSubscriptionBalanceLcy: rawData.advanceSubscriptionBalanceLcy,
      outsideOfCommitmentPaidInCapitalLcy:
        rawData.outsideOfCommitmentPaidInCapitalLcy,
    },
    ...mapFees(fees),
    ...mapHoldings({
      liveHoldings,
      liveHoldingsByAssetSummary,
      liveHoldingsTotals,
      realisedHoldings,
      realisedHoldingsByAssetSummary,
      realisedHoldingsTotals,
    }),
    incomeAndExpenses: mapIncomeAndExpenses(incomesAndExpenses),
    preferredReturnBalance: mapPreferredReturnBalance(preferredReturn),
    capitalBalanceAndReconciledPayments:
      mapCapitalBalanceAndReconciledPayments(reconciledActions),
    capitalActions: mapCapitalActions(capitalActions),
    closeHistory: {
      data: fundCloseHistory.map((entry) => ({
        id: `${entry.commitmentId}-${entry.date.toString}`,
        date: entry.date,
        name: entry.date.toReadable(),
        netEqualisationInterestPaidLcy: entry.equalisationInterestAmountLcy,
        newCommitmentLcy: entry.totalCommitmentLcy,
      })),
      totals: fundCloseHistory.reduce(
        (totals, entry) => {
          return {
            commitmentValueLcy: round(
              totals.commitmentValueLcy + entry.totalCommitmentLcy,
              2,
            ),
            netEqualisationInterestLcy: round(
              totals.netEqualisationInterestLcy +
                entry.equalisationInterestAmountLcy,
              2,
            ),
          };
        },
        { commitmentValueLcy: 0, netEqualisationInterestLcy: 0 },
      ),
    },
  };
};
